import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Link } from '@air/next-icons';
import { Button, type ButtonProps } from '@air/primitive-button';
import { Tooltip } from '@air/primitive-tooltip';

import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { selectedBoardIdsForCollectionSelector, selectedClipsAssetIdsSelector } from '~/store/selectedItems/selectors';
import { canViewAsset } from '~/utils/permissions/assetPermissions';
import { canViewBoard } from '~/utils/permissions/boardPermissions';
import { canCreateCollection } from '~/utils/permissions/workspacePermissions';

export type ShareCollectionButtonProps = Pick<Required<ButtonProps>, 'onClick'> & Pick<ButtonProps, 'color'>;

export const ShareCollectionButton = memo(({ onClick, color = 'blue' }: ShareCollectionButtonProps) => {
  const assetIds = useSelector(selectedClipsAssetIdsSelector);
  const boardIds = useSelector(selectedBoardIdsForCollectionSelector);

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const assetPermissionCache = useAssetPermissionsCache();
  const boardPermissionCache = useBoardPermissionsCache();

  const showShareCollectionButton = useMemo(() => {
    return (
      canCreateCollection(workspacePermissions) &&
      assetIds.every((assetId) => canViewAsset(assetPermissionCache.getAssetPermissions(assetId))) &&
      boardIds.every((boardId) => canViewBoard(boardPermissionCache.getBoardPermissions(boardId)))
    );
  }, [workspacePermissions, assetIds, boardIds, assetPermissionCache, boardPermissionCache]);

  if (!showShareCollectionButton) {
    return null;
  }

  return (
    <Tooltip label="Share">
      <Button prefix={<Link className="size-4" />} appearance="filled" color={color} onClick={onClick} size="small">
        Share {assetIds.length + boardIds.length} items
      </Button>
    </Tooltip>
  );
});

ShareCollectionButton.displayName = 'ShareCollectionButton';
